/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Dialog, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StyledButton from '../buttons/StyledButton';
import { SubmitHandler, useForm } from 'react-hook-form';
import { TextFieldFormInput } from '../formInputs';
import { useIsOnline } from '../../hooks';

interface MoveToStagingFormProps {
  open: boolean;
  onSubmit: (data: MoveToStagingModel) => void;
  onClose: () => void;
  onBack: () => void;
}

export interface MoveToStagingModel {
  location: string;
}

export default function MoveToStagingForm({ open, onSubmit, onClose, onBack }: MoveToStagingFormProps) {
  const theme = useTheme();
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<MoveToStagingModel>();
  const { isOnline } = useIsOnline();
  const onSubmitForm: SubmitHandler<MoveToStagingModel> = (data) => onSubmit(data);

  return (
    <Dialog open={open} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <Stack spacing={0}>
          {/* Header */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: theme.palette.mode === 'light' ? 'grey.50' : 'grey.900',
              borderBottom: `1px solid ${theme.palette.divider}`
            }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <IconButton onClick={onBack} size="small" sx={{ marginRight: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1 }}>
                <WarehouseIcon sx={{ fontSize: 24, color: 'text.primary' }} />
                <Typography variant="h6" fontWeight={600}>
                  Move to Staging
                </Typography>
              </Stack>
              <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Content */}
          <Box sx={{ padding: 3, backgroundColor: 'grey.50' }}>
            <Stack spacing={3}>
              {/* Info Card */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  padding: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <Stack direction="row" spacing={2} alignItems="flex-start">
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      backgroundColor: 'info.lighter',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                    <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Staging Location Tracking
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      In order to track SSCCs in the DC, please select the appropriate staging location.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Location Input Card */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  padding: 2.5,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <WarehouseIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={500}>
                    Staging Location (Optional)
                  </Typography>
                </Stack>
                <TextFieldFormInput name="location" label="" control={control} error={errors['location']} />
              </Box>
            </Stack>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              padding: 2.5,
              paddingX: 3,
              backgroundColor: 'white',
              borderTop: `1px solid ${theme.palette.divider}`,
              boxShadow: '0 -2px 8px rgba(0,0,0,0.08)'
            }}>
            {isOnline ? (
              <StyledButton
                variant="contained"
                type="submit"
                fullWidth
                startIcon={<LocalShippingIcon />}
                sx={{
                  paddingY: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 2
                }}>
                Complete + Print
              </StyledButton>
            ) : (
              <Tooltip title="You are offline. This feature is not available when offline." enterTouchDelay={0}>
                <span style={{ width: '100%' }}>
                  <StyledButton
                    variant="contained"
                    disabled
                    fullWidth
                    startIcon={<LocalShippingIcon />}
                    sx={{
                      paddingY: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 2
                    }}>
                    Complete + Print
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        </Stack>
      </form>
    </Dialog>
  );
}
